<template>
  <div class="flex flex-col gap-6">
    <div
      v-if="isDNCFeatureEnabled"
      class="flex flex-row items-baseline gap-4 p-3 rounded-lg bg_2 border_4">
      <div class="py-1">
        <a-switch
          size="small"
          :checked="isGlobalDNCEnabled"
          @change="toggleDNC"
        />
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-base font-semibold">Follow US DNC List</span>
        <span class="text-[13px] font-normal secondary_color"
          >Block Calls to numbers on the National Do Not Call Registry for all US outreach</span
        >
      </div>
    </div>

    <div v-if="loading" class="flex flex-col items-center justify-center gap-2 mt-20">
      <CustomLoader :size="28" />
    </div>
    
    <div v-else>
      <EmptyScreen
          source="block_list"
          text="No phone numbers found!"
          v-if="phoneBlockList?.length === 0"
      />
      <CustomTable
          v-else
          :height="isDNCFeatureEnabled ? '50vh' : '60vh'"
          :headers="phoneHeaders"
          :data="phoneBlockList"
          :custom-column-render="phoneBlockListCustomColumnRender"
          :actions="phoneActions"
          :show-actions-column="true"
          :pagination="pagination"
          @refresh="fetchPhoneBlockList"
          @update:pagination="handlePageChange"
      />
    </div>

    <CustomModal
      v-model:visible="showEditModal"
      title="Edit DND status"
      width="30%"
      @close="handleModalClose"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label for="edit-phone-number" class="text-sm secondary_color">Phone Number</label>
          <span id="edit-phone-number" class="text-sm text_color">{{ editingPhone.number }}</span>
        </div>

        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <label for="edit-dnd-status" class="text-sm secondary_color">DND status</label>
          </div>
          
          <a-select
            id="edit-dnd-status"
            v-model:value="editingPhone.dnd_status"
            class="w-full"
            placeholder="Select DND option"
            :options="dndStatusOptions"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW_GREY"
                alt="bottom_grey_arrow"
                class="size-4"
              />
            </template>
          </a-select>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <a-button
            class="secondary_medium"
            @click="showEditModal = false"
          >
            Cancel
          </a-button>
          <a-button
            class="primary_medium"
            @click="handleSaveDNDStatus"
            :loading="saving"
          >
            Save
          </a-button>
        </div>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import debounce from "lodash/debounce";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { phoneBlockListHeaders, dndStatusOptions, dndStatusDisplayMap, phoneBlockListCustomColumnRender } from "@/src/constants/callConstants.js";
import { getCallSetting, setCallSetting, removeFromDNCBlocklist } from "@/src/utils/callsUtils.js";
import { callSettingKeys } from "@/src/constants/callConstants.js";
import CallService from "@/src/api/CallService.js";
import { formatDateTime } from "@/src/utils/utils.js";

const props = defineProps({
  searchQuery: {
    type: String,
    default: ''
  }
});

const phoneHeaders = phoneBlockListHeaders;

const phoneActions = [
  {
    label: "Edit DND status",
    handler: (row) => handleEditDNDStatus(row),
    icon: imageNames.EDIT,
  },
  {
    label: "Remove",
    handler: (row) => {
      handleRemove(row);
      setTimeout(() => document.dispatchEvent(new Event('click')), 50);
    },
    icon: imageNames.DELETE_GREY,
  },
];

const phoneBlockList = ref([]);
const loading = ref(true);
const showEditModal = ref(false);
const saving = ref(false);
const isGlobalDNCEnabled = ref(false);
const pagination = ref({
  current: 1,
  pageSize: 20,
  total: 0,
});
const editingPhone = ref({
  id: null,
  number: '',
  dnd_status: ''
});

const isDNCFeatureEnabled = computed(() => {
  return localStorage.getItem('dnc') === 'true';
});

const fetchPhoneBlockList = async () => {
  loading.value = true;

  try {
    const params = {
      page: pagination.value.current,
      page_size: pagination.value.pageSize
    };
    
    if (props.searchQuery) {
      params.search_query = props.searchQuery;
    }
    
    const response = await CallService.getDNDList(params);
    if (response.data) {
      phoneBlockList.value = response.data.map(item => {
        const entityFirstName = item?.entity?.first_name || '';
        const entityLastName = item?.entity?.last_name || '';
        
        return {
          id: item.id,
          name: `${entityFirstName} ${entityLastName}`.trim() || 'Unknown',
          profile_pic: item?.entity?.profile_pic,
          entity_data: item?.entity,
          number: item.number,
          dnd_status: dndStatusDisplayMap[item.dnd_status] || item.dnd_status,
          updated_on: formatDateTime(item.updated_at, 'date'),
          updated_by: item?.updated_by?.first_name + " " + item?.updated_by?.last_name,
          profile_icon: item?.updated_by?.profile_pic,
          user_data: item?.updated_by, 
          raw_dnd_status: item.dnd_status 
        };
      });
      
      pagination.value.total = response.total_count || 0;
    }
  } catch (error) {
    console.error("Error fetching phone block list:", error);
    message.error("Failed to load phone numbers");
  } finally {
    loading.value = false;
  }
};

const handleEditDNDStatus = (row) => {
  editingPhone.value = {
    id: row.id,
    number: row.number,
    dnd_status: row.raw_dnd_status || (row.dnd_status === 'Do Not Call' ? 'do_not_call' : 'whitelist')
  };
  showEditModal.value = true;
};

const handleSaveDNDStatus = async () => {
  try {
    saving.value = true;
    
    const response = await CallService.updateDNDStatus({
      number: editingPhone.value.number,
      dnd_status: editingPhone.value.dnd_status
    });
    
    if (response.success) {
      message.success("DND status updated successfully");
      showEditModal.value = false;
      await fetchPhoneBlockList();
    } else {
      throw new Error('Failed to update DND status');
    }
  } catch (error) {
    console.error("Error updating DND status:", error);
    message.error( "Failed to update DND status");
  } finally {
    saving.value = false;
  }
};

const handleModalClose = () => {
  editingPhone.value = {
    id: null,
    number: '',
    dnd_status: ''
  };
};

const handleRemove = async (row) => {
  try {
    const response = await removeFromDNCBlocklist(row.number);
    
    if (response.success) {
      message.success("Removed from block list successfully");
      await fetchPhoneBlockList();
    } else {
      throw new Error('Failed to remove from block list');
    }
  } catch (error) {
    console.error("Error while removing from block list:", error);
    message.error("Failed to remove from block list");
  }
};

const handlePageChange = async (newPagination) => {
  try {
    pagination.value = newPagination;
    await fetchPhoneBlockList();
  } catch (error) {
    console.error("An error occurred while handling page change: ", error);
  }
};

const debouncedFetch = debounce(() => {
  pagination.value.current = 1; 
  fetchPhoneBlockList();
}, 300);

const fetchDNCStatus = async () => {
  if (!isDNCFeatureEnabled.value) {
    return;
  }
  try {
    const status = await getCallSetting(callSettingKeys.DNC);
    isGlobalDNCEnabled.value = status;
  } catch (error) {
    console.error("Failed to fetch DNC status:", error);
  }
};

const toggleDNC = async (checked) => {
  try {
    if (!isDNCFeatureEnabled.value) {
    return;
    }
    const success = await setCallSetting(callSettingKeys.DNC, checked);
    if (success) {
      isGlobalDNCEnabled.value = checked;
      message.success(`US DNC List ${checked ? 'enabled' : 'disabled'} successfully`);
    } else {
      message.error('Failed to update DNC status');
      isGlobalDNCEnabled.value = !checked;
    }
  } catch (error) {
    console.error("Failed to toggle DNC status:", error);
    message.error('Failed to update DNC status');
    isGlobalDNCEnabled.value = !checked;
  }
};

watch(() => props.searchQuery, () => {
  debouncedFetch();
});

defineExpose({
  fetchPhoneBlockList
});

onMounted(() => {
  fetchPhoneBlockList();
  fetchDNCStatus();
});
</script>

